import React, {FC, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {Pill} from '../../../../../../libs/shared/ui/src/components/pill/pill';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {makeSizeConstants} from '../../../../../../libs/shared/ui/src/styles/styleHelpers';

/*
 * Props.
 */

interface StyledActivityConversationStageUpdatePillProps {
  $backgroundColor?: string;
  $borderColor?: string;
  $color?: string;
  $hoverColor?: string;
  $size: VisualSizesEnum;
  $showBottomChevron?: boolean;
}

interface ActivityConversationStageUpdatePillProps {
  conversationStageTypeLabel: ReactNode;
  color: string;
  borderColor: string;
  size?: VisualSizesEnum;
  backgroundColor: string;
  hoverColor?: string;
  showBottomChevron?: boolean;
}

/*
 * Style.
 */

const pillSizeStyles = makeSizeConstants(
  css`
    font-size: ${fontSizes.veryTiny};
    line-height: ${fontSizes.tiny};
    padding: 2px 5px;
  `,
  css`
    font-size: ${fontSizes.verySmall};
    line-height: ${fontSizes.verySmall};
    padding: 3px 5px;
  `,
);

const StyledActivityConversationStageUpdatePillStyle = (
  props: StyledActivityConversationStageUpdatePillProps,
) => css`
  ${pillSizeStyles[props.$size]}
  background-color: ${props.$backgroundColor};
  border: 1px solid ${props.$borderColor};
  color: ${props.$color};
  ${props.$showBottomChevron ? `padding-left: 8px;` : ``}

  &:hover {
    background-color: ${props.$hoverColor};
  }
`;

const StyledActivityConversationStageUpdatePill = styled(
  Pill,
)<StyledActivityConversationStageUpdatePillProps>`
  ${StyledActivityConversationStageUpdatePillStyle}
  display: flex;
  align-items: center;
`;

const StyledStageTypeLabelWrapperDiv = styled.div`
  &::first-letter {
    text-transform: uppercase;
  }
`;

const StyledChevronIcon = styled(Icon)`
  margin-left: 4px;
  fill: none; // Needed otherwise the icon fills iself with currentColor for some reason.
`;

/*
 * Component.
 */

export const ActivityConversationStageUpdatePill: FC<ActivityConversationStageUpdatePillProps> = ({
  conversationStageTypeLabel,
  color,
  borderColor,
  backgroundColor,
  hoverColor,
  showBottomChevron,
  size = VisualSizesEnum.MEDIUM,
}) => (
  <StyledActivityConversationStageUpdatePill
    $color={color}
    $borderColor={borderColor}
    $backgroundColor={backgroundColor}
    $hoverColor={hoverColor}
    $size={size}
    $showBottomChevron={showBottomChevron}
  >
    <StyledStageTypeLabelWrapperDiv>{conversationStageTypeLabel}</StyledStageTypeLabelWrapperDiv>
    {showBottomChevron && <StyledChevronIcon name="chevronDownTiny" color={color} />}
  </StyledActivityConversationStageUpdatePill>
);
